import { useEffect, useMemo, useState } from "react";
import {
  Monitor,
  Smartphone,
  GraduationCap,
  Play,
  Sun,
  Trophy,
  Crown,
  Medal,
  Award,
  ArrowRight,
  BookOpen,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

export const FONT_OPTIONS = [
  { value: "Inter", label: "Inter", subtitle: "Modern", css: "'Inter', sans-serif" },
  { value: "Playfair Display", label: "Playfair Display", subtitle: "Klassiek", css: "'Playfair Display', serif" },
  { value: "Poppins", label: "Poppins", subtitle: "Vriendelijk", css: "'Poppins', sans-serif" },
  { value: "Raleway", label: "Raleway", subtitle: "Elegant", css: "'Raleway', sans-serif" },
  { value: "DM Sans", label: "DM Sans", subtitle: "Neutraal", css: "'DM Sans', sans-serif" },
  { value: "Merriweather", label: "Merriweather", subtitle: "Serieus", css: "'Merriweather', serif" },
  { value: "Nunito", label: "Nunito", subtitle: "Speels", css: "'Nunito', sans-serif" },
  { value: "Space Grotesk", label: "Space Grotesk", subtitle: "Tech", css: "'Space Grotesk', sans-serif" },
];

export const LAYOUT_OPTIONS = [
  { value: "cards", label: "Kaarten", description: "Grid van kaarten" },
  { value: "list", label: "Lijst", description: "Thumbnail + tekst" },
  { value: "minimal", label: "Minimaal", description: "Veel witruimte" },
];

export const HEADER_OPTIONS = [
  { value: "solid", label: "Vol", description: "Gevulde achtergrond" },
  { value: "transparent", label: "Transparant", description: "Verschijnt bij scroll" },
  { value: "centered", label: "Gecentreerd", description: "Logo midden, nav onder" },
];

export const BUTTON_RADIUS_OPTIONS = [
  { value: "rounded", label: "Rond", radius: "9999px" },
  { value: "soft", label: "Zacht", radius: "10px" },
  { value: "sharp", label: "Recht", radius: "2px" },
];

export const radiusFor = (key) =>
  BUTTON_RADIUS_OPTIONS.find((o) => o.value === key)?.radius ?? "10px";

export const fontCssFor = (key) =>
  FONT_OPTIONS.find((o) => o.value === key)?.css ?? FONT_OPTIONS[0].css;

const FONT_URL_MAP = {
  Inter: "Inter:wght@400;500;600;700",
  Poppins: "Poppins:wght@400;500;600;700",
  Raleway: "Raleway:wght@400;500;600;700",
  "DM Sans": "DM+Sans:wght@400;500;600;700",
  Merriweather: "Merriweather:wght@400;700",
  Nunito: "Nunito:wght@400;500;600;700",
  "Space Grotesk": "Space+Grotesk:wght@400;500;600;700",
  "Playfair Display": "Playfair+Display:wght@400;600;700",
};

const injectedFonts = new Set();
const injectFont = (fontName) => {
  if (typeof document === "undefined") return;
  if (!fontName || injectedFonts.has(fontName)) return;
  const family = FONT_URL_MAP[fontName];
  if (!family) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${family}&display=swap`;
  link.dataset.platformPreviewFont = fontName;
  document.head.appendChild(link);
  injectedFonts.add(fontName);
};

const tokensForTheme = (isDark) =>
  isDark
    ? {
        bg: "#06070a",
        bgSoft: "#0b0d12",
        bgElevated: "#101218",
        fg: "#f7f7f8",
        fgMuted: "#a4a5ad",
        fgSubtle: "#6a6b73",
        border: "rgba(255,255,255,0.06)",
        borderStrong: "rgba(255,255,255,0.12)",
        surface1: "rgba(255,255,255,0.035)",
        surface2: "rgba(255,255,255,0.065)",
      }
    : {
        bg: "#f4f5f8",
        bgSoft: "#fafafc",
        bgElevated: "#ffffff",
        fg: "#0b0c10",
        fgMuted: "#5b5d68",
        fgSubtle: "#8d8f9a",
        border: "rgba(11,12,16,0.07)",
        borderStrong: "rgba(11,12,16,0.13)",
        surface1: "rgba(11,12,16,0.025)",
        surface2: "rgba(11,12,16,0.045)",
      };

const mix = (color, pct, transparent = true) =>
  `color-mix(in srgb, ${color} ${pct}%, ${transparent ? "transparent" : "white"})`;

const MOCK_COURSES = [
  {
    title: "Introductie tot Marketing",
    summary: "Leer de fundamenten van moderne marketing.",
    level: "Beginner",
    lessons: 12,
    students: 87,
    price: 49,
  },
  {
    title: "Conversie Optimalisatie",
    summary: "Maak van bezoekers betalende klanten.",
    level: "Gevorderd",
    lessons: 18,
    students: 134,
    price: 89,
  },
  {
    title: "Content Strategie",
    summary: "Bouw een publiek dat terugkomt.",
    level: "Beginner",
    lessons: 9,
    students: 52,
    price: 59,
  },
];

const MOCK_LEADERBOARD = [
  { rank: 1, name: "Eva Janssens", points: 1240 },
  { rank: 2, name: "Mathias Peeters", points: 980 },
  { rank: 3, name: "Lien Devos", points: 815 },
];

const TopBar = ({ settings, tokens, fontCss, logoSrc, isMobile }) => {
  const transparent = settings.header_style === "transparent";
  const centered = settings.header_style === "centered";
  const platformName = settings.platform_name || "Jouw platform";

  return (
    <div
      style={{
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: centered ? "center" : "space-between",
        padding: "0 16px",
        background: transparent ? "transparent" : tokens.bgElevated,
        borderBottom: `1px solid ${tokens.border}`,
        fontFamily: fontCss,
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {logoSrc ? (
          <img
            src={logoSrc}
            alt=""
            style={{ height: 26, width: "auto", objectFit: "contain" }}
          />
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 28,
              width: 28,
              borderRadius: 8,
              background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <GraduationCap size={15} />
          </span>
        )}
        <span
          style={{
            fontWeight: 600,
            fontSize: 14,
            color: tokens.fg,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {platformName}
        </span>
      </div>

      {!centered && !isMobile && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            color: tokens.fgMuted,
            fontSize: 12,
          }}
        >
          <span
            style={{
              height: 26,
              width: 26,
              borderRadius: 999,
              background: tokens.surface2,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: tokens.fgMuted,
            }}
          >
            <Sun size={13} />
          </span>
          <span
            style={{
              height: 28,
              width: 28,
              borderRadius: 999,
              background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            JD
          </span>
        </div>
      )}
    </div>
  );
};

const HeroSection = ({ settings, tokens, fontCss, isMobile }) => {
  const heroTitle = settings.hero_title || settings.platform_name || "Leer in je eigen tempo";
  const heroSubtitle =
    settings.hero_subtitle ||
    settings.tagline ||
    "Praktijkgerichte cursussen, een actieve community en gepersonaliseerde feedback.";
  const ctaText = settings.hero_cta_text || "Start vandaag";
  const radius = radiusFor(settings.button_radius);

  return (
    <section
      style={{
        position: "relative",
        borderRadius: 18,
        border: `1px solid ${tokens.border}`,
        background: tokens.bgElevated,
        padding: isMobile ? "22px 18px" : "32px 28px",
        overflow: "hidden",
        fontFamily: fontCss,
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.85,
          pointerEvents: "none",
          background: `radial-gradient(900px 360px at 0% 0%, ${mix(settings.primary_color, 22)}, transparent 70%), radial-gradient(700px 320px at 100% 100%, ${mix(settings.accent_color, 14)}, transparent 70%)`,
        }}
      />
      <div style={{ position: "relative", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: 22, alignItems: "center" }}>
        <div>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: tokens.fgSubtle,
              marginBottom: 8,
            }}
          >
            Welkom terug
          </div>
          <h1
            style={{
              fontSize: isMobile ? 22 : 30,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              color: tokens.fg,
              margin: 0,
              marginBottom: 8,
            }}
          >
            {heroTitle}
          </h1>
          <p
            style={{
              fontSize: 13,
              color: tokens.fgMuted,
              maxWidth: 380,
              margin: 0,
              marginBottom: 14,
              lineHeight: 1.55,
            }}
          >
            {heroSubtitle}
          </p>
          <button
            type="button"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "9px 16px",
              fontSize: 12.5,
              fontWeight: 600,
              color: "#fff",
              background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
              borderRadius: radius,
              border: "none",
              cursor: "default",
            }}
          >
            {ctaText} <ArrowRight size={12} />
          </button>
        </div>

        {!isMobile && (
          <div
            style={{
              borderRadius: 14,
              border: `1px solid ${tokens.border}`,
              background: tokens.surface1,
              padding: 18,
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                color: tokens.fgSubtle,
                marginBottom: 8,
              }}
            >
              Voortgang
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 34, fontWeight: 600, color: tokens.fg, letterSpacing: "-0.02em" }}>62</span>
              <span style={{ fontSize: 16, color: tokens.fgMuted }}>%</span>
            </div>
            <div
              style={{
                height: 4,
                borderRadius: 999,
                background: tokens.surface2,
                marginTop: 10,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: "62%",
                  background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
                }}
              />
            </div>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                color: tokens.fgMuted,
              }}
            >
              <span>18 / 29 lessen</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: settings.accent_color }}>
                <Trophy size={11} /> 320 punten
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const StatsRow = ({ tokens, fontCss, isMobile }) => {
  const items = [
    { label: "Mijn cursussen", value: 4, sub: "29 lessen totaal", Icon: BookOpen },
    { label: "Actief", value: 2, sub: "18 lessen afgerond", Icon: Sparkles, accent: true },
    { label: "Voltooid", value: 1, sub: "25% van je cursussen", Icon: CheckCircle2 },
  ];
  return (
    <section
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
        gap: 10,
        fontFamily: fontCss,
      }}
    >
      {items.map((it) => (
        <div
          key={it.label}
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 16,
            border: `1px solid ${tokens.border}`,
            background: tokens.bgElevated,
            padding: 18,
          }}
        >
          <it.Icon
            size={96}
            style={{
              position: "absolute",
              right: -16,
              bottom: -16,
              opacity: 0.04,
              color: tokens.fg,
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative", display: "flex", justifyContent: "space-between" }}>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                color: tokens.fgSubtle,
              }}
            >
              {it.label}
            </span>
            {it.accent && (
              <span
                style={{
                  height: 8,
                  width: 8,
                  borderRadius: 999,
                  background: "#4ade80",
                  boxShadow: "0 0 8px 2px rgba(74,222,128,0.4)",
                }}
              />
            )}
          </div>
          <div
            style={{
              position: "relative",
              marginTop: 6,
              fontSize: 32,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: tokens.fg,
            }}
          >
            {it.value}
          </div>
          <div style={{ position: "relative", marginTop: 6, fontSize: 12, color: tokens.fgMuted }}>{it.sub}</div>
        </div>
      ))}
    </section>
  );
};

const CourseCardMock = ({ course, tokens, fontCss, settings, mode }) => {
  const radius = radiusFor(settings.button_radius);
  const isList = mode === "list";
  const isMinimal = mode === "minimal";

  if (isMinimal) {
    return (
      <div
        style={{
          padding: "16px 0",
          borderBottom: `1px solid ${tokens.border}`,
          fontFamily: fontCss,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: tokens.fgSubtle,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {course.level}
        </div>
        <h3
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: tokens.fg,
            marginBottom: 4,
          }}
        >
          {course.title}
        </h3>
        <p style={{ margin: 0, fontSize: 13, color: tokens.fgMuted, lineHeight: 1.5 }}>{course.summary}</p>
        <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
          <span
            style={{
              background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 600,
            }}
          >
            € {course.price}
          </span>
          <span style={{ color: tokens.fgSubtle }}>{course.lessons} lessen</span>
        </div>
      </div>
    );
  }

  if (isList) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: 12,
          borderRadius: 14,
          border: `1px solid ${tokens.border}`,
          background: tokens.bgElevated,
          fontFamily: fontCss,
        }}
      >
        <div
          style={{
            height: 52,
            width: 84,
            borderRadius: 10,
            background: `linear-gradient(135deg, ${mix(settings.primary_color, 20)}, ${mix(settings.accent_color, 10)})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BookOpen size={18} style={{ color: tokens.fgMuted }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: tokens.fg }}>{course.title}</div>
          <div
            style={{
              fontSize: 11.5,
              color: tokens.fgMuted,
              marginTop: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {course.summary}
          </div>
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            flexShrink: 0,
          }}
        >
          € {course.price}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: 16,
        border: `1px solid ${tokens.border}`,
        background: tokens.bgElevated,
        overflow: "hidden",
        fontFamily: fontCss,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "16 / 10",
          background: `linear-gradient(135deg, ${mix(settings.primary_color, 22)}, ${mix(settings.accent_color, 12)})`,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to top, rgba(0,0,0,0.55), transparent 60%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 10,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 32,
            width: 32,
            borderRadius: 999,
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.25)",
            backdropFilter: "blur(8px)",
            color: "#fff",
          }}
        >
          <Play size={12} style={{ transform: "translateX(1px)" }} fill="currentColor" />
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 10,
            right: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: 999,
            fontSize: 10,
            background: "rgba(0,0,0,0.4)",
            color: "#fff",
            backdropFilter: "blur(6px)",
          }}
        >
          {course.lessons} lessen
        </div>
      </div>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            alignSelf: "flex-start",
            padding: "2px 8px",
            borderRadius: 999,
            border: `1px solid ${tokens.border}`,
            fontSize: 10.5,
            color: tokens.fgMuted,
          }}
        >
          {course.level}
        </span>
        <h3
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.25,
            color: tokens.fg,
            letterSpacing: "-0.01em",
          }}
        >
          {course.title}
        </h3>
        <p style={{ margin: 0, fontSize: 12, color: tokens.fgMuted, lineHeight: 1.5 }}>{course.summary}</p>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
            borderTop: `1px solid ${tokens.border}`,
            marginTop: 2,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            € {course.price}
          </span>
          <span
            style={{
              fontSize: 11,
              color: tokens.fgSubtle,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            Bekijk <ArrowRight size={11} />
          </span>
        </div>
      </div>
    </div>
  );
};

const CoursesSection = ({ settings, tokens, fontCss, isMobile }) => {
  const mode = settings.layout_style || "cards";

  if (mode === "minimal") {
    return (
      <section style={{ fontFamily: fontCss }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: tokens.fg, letterSpacing: "-0.02em", marginBottom: 12 }}>
          Cursussen
        </h2>
        <div>
          {MOCK_COURSES.map((c) => (
            <CourseCardMock
              key={c.title}
              course={c}
              tokens={tokens}
              fontCss={fontCss}
              settings={settings}
              mode="minimal"
            />
          ))}
        </div>
      </section>
    );
  }

  if (mode === "list") {
    return (
      <section style={{ fontFamily: fontCss }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tokens.fg, letterSpacing: "-0.01em", marginBottom: 10 }}>
          Cursussen
        </h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {MOCK_COURSES.map((c) => (
            <CourseCardMock
              key={c.title}
              course={c}
              tokens={tokens}
              fontCss={fontCss}
              settings={settings}
              mode="list"
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section style={{ fontFamily: fontCss }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tokens.fg, letterSpacing: "-0.01em", marginBottom: 10 }}>
        Cursussen
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: 10,
        }}
      >
        {MOCK_COURSES.map((c) => (
          <CourseCardMock
            key={c.title}
            course={c}
            tokens={tokens}
            fontCss={fontCss}
            settings={settings}
            mode="cards"
          />
        ))}
      </div>
    </section>
  );
};

const RankIcon = ({ rank }) => {
  if (rank === 1) return <Crown size={12} />;
  if (rank === 2) return <Medal size={12} />;
  if (rank === 3) return <Award size={12} />;
  return null;
};

const LeaderboardWidget = ({ tokens, fontCss, settings }) => (
  <section
    style={{
      borderRadius: 16,
      border: `1px solid ${tokens.border}`,
      background: tokens.bgElevated,
      overflow: "hidden",
      fontFamily: fontCss,
    }}
  >
    <div
      style={{
        padding: "14px 16px 8px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: tokens.fg, letterSpacing: "-0.01em" }}>
        Top studenten
      </h3>
      <span style={{ fontSize: 11, color: tokens.fgMuted, display: "inline-flex", alignItems: "center", gap: 3 }}>
        Alles <ArrowRight size={10} />
      </span>
    </div>
    <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
      {MOCK_LEADERBOARD.map((entry) => {
        const rankBg =
          entry.rank === 1
            ? "rgba(234,179,8,0.15)"
            : entry.rank === 2
              ? "rgba(161,161,170,0.18)"
              : "rgba(249,115,22,0.18)";
        const rankFg =
          entry.rank === 1 ? "#facc15" : entry.rank === 2 ? "#e4e4e7" : "#fb923c";
        return (
          <li
            key={entry.rank}
            style={{
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              borderTop: `1px solid ${tokens.border}`,
            }}
          >
            <span
              style={{
                height: 26,
                width: 26,
                borderRadius: 8,
                background: rankBg,
                color: rankFg,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              <RankIcon rank={entry.rank} />
            </span>
            <span
              style={{
                height: 26,
                width: 26,
                borderRadius: 999,
                background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.accent_color})`,
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              {entry.name
                .split(" ")
                .map((p) => p[0])
                .slice(0, 2)
                .join("")}
            </span>
            <span style={{ flex: 1, fontSize: 12.5, color: tokens.fg, fontWeight: 500 }}>{entry.name}</span>
            <span style={{ fontSize: 12, color: tokens.fgMuted, fontVariantNumeric: "tabular-nums" }}>
              {entry.points.toLocaleString("nl-BE")}
            </span>
          </li>
        );
      })}
    </ol>
  </section>
);

const PreviewBody = ({ settings, tokens, fontCss, isMobile }) => (
  <div
    style={{
      background: `radial-gradient(1200px 600px at 0% -10%, ${mix(settings.primary_color, 10)}, transparent 60%), radial-gradient(900px 500px at 100% 0%, ${mix(settings.accent_color, 7)}, transparent 65%), ${tokens.bg}`,
      padding: isMobile ? "16px 14px" : "22px 22px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      minHeight: 540,
    }}
  >
    <HeroSection settings={settings} tokens={tokens} fontCss={fontCss} isMobile={isMobile} />
    <StatsRow tokens={tokens} fontCss={fontCss} isMobile={isMobile} />
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1.5fr 1fr",
        gap: 16,
        alignItems: "start",
      }}
    >
      <CoursesSection settings={settings} tokens={tokens} fontCss={fontCss} isMobile={isMobile} />
      <LeaderboardWidget tokens={tokens} fontCss={fontCss} settings={settings} />
    </div>
  </div>
);

const PlatformPreview = ({ settings, isDark, previewDark = true }) => {
  const [viewport, setViewport] = useState("desktop");
  const isMobile = viewport === "mobile";

  useEffect(() => {
    injectFont(settings.font_family);
  }, [settings.font_family]);

  const tokens = useMemo(() => tokensForTheme(!!previewDark), [previewDark]);
  const fontCss = fontCssFor(settings.font_family);
  const logoSrc = useMemo(() => {
    if (settings.logoUrl) return settings.logoUrl;
    if (typeof settings.logo === "string" && (settings.logo.startsWith("data:") || settings.logo.startsWith("http"))) {
      return settings.logo;
    }
    return null;
  }, [settings.logoUrl, settings.logo]);

  const host = settings.subdomain
    ? `${settings.subdomain}.leerplatform.zichtbaarstudio.be`
    : "jouw-platform.leerplatform.zichtbaarstudio.be";

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <span
          className={`text-[11px] font-medium uppercase tracking-wide ${
            isDark ? "text-gray-500" : "text-gray-500"
          }`}
        >
          Live preview
        </span>
        <div
          className={`flex gap-1 p-0.5 rounded-md ${
            isDark ? "bg-white/[0.06]" : "bg-black/[0.06]"
          }`}
        >
          <button
            type="button"
            onClick={() => setViewport("desktop")}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] ${
              viewport === "desktop"
                ? isDark
                  ? "bg-white/[0.1] text-white"
                  : "bg-white text-gray-900 shadow-sm"
                : isDark
                  ? "text-gray-400"
                  : "text-gray-500"
            }`}
          >
            <Monitor size={11} /> Desktop
          </button>
          <button
            type="button"
            onClick={() => setViewport("mobile")}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] ${
              viewport === "mobile"
                ? isDark
                  ? "bg-white/[0.1] text-white"
                  : "bg-white text-gray-900 shadow-sm"
                : isDark
                  ? "text-gray-400"
                  : "text-gray-500"
            }`}
          >
            <Smartphone size={11} /> Mobiel
          </button>
        </div>
      </div>

      <div
        className={`flex-1 rounded-lg border overflow-hidden ${
          isDark
            ? "bg-white/[0.02] border-white/[0.06]"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-black/5 bg-white">
          <div className="h-2 w-2 rounded-full bg-red-300" />
          <div className="h-2 w-2 rounded-full bg-yellow-300" />
          <div className="h-2 w-2 rounded-full bg-green-300" />
          <div className="ml-3 flex-1 text-[10px] text-gray-400 truncate font-mono">{host}</div>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: 620 }}>
          <div
            data-theme={previewDark ? "dark" : "light"}
            style={{
              margin: "0 auto",
              width: isMobile ? 390 : "100%",
              maxWidth: "100%",
              transition: "width 0.25s ease",
              background: tokens.bg,
              color: tokens.fg,
            }}
          >
            <TopBar
              settings={settings}
              tokens={tokens}
              fontCss={fontCss}
              logoSrc={logoSrc}
              isMobile={isMobile}
            />
            <PreviewBody
              settings={settings}
              tokens={tokens}
              fontCss={fontCss}
              isMobile={isMobile}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformPreview;
